import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AlertModal, ConfirmModal } from './AlertModal';
import { IconEdit, IconTrash, IconClipboard, IconSwords, IconBarChart } from './Icons';
import { formatLocalDate } from '../utils/timeUtils';
import './PublicMatches.css';
import GlowCard from './GlowCard';
import { useTranslation } from 'react-i18next';
import CreatedBy from './CreatedBy';
import CreateSeasonModal from './seasons/CreateSeasonModal';
import { getPrefixColorOption } from './seasons/seasonForm';

function VetoBadge({ vetoStyle }) {
  const style = vetoStyle || 'vrs';
  const label = `Veto · ${style === 'faceit' ? 'Faceit' : 'VRS'}`;

  return (
    <span style={{
      padding: '0.2rem 0.55rem', borderRadius: 'var(--radius-sm)', fontSize: '0.7rem', fontWeight: 600,
      background: 'rgba(139,92,246,0.08)', color: '#8b5cf6',
      border: '1px solid rgba(139,92,246,0.15)',
      flexShrink: 0, textTransform: 'uppercase', letterSpacing: '0.04em',
      cursor: 'default',
    }}>{label}</span>
  );
}

/**
 * Seasons list. Each season has its own page (/admin/seasons/:id — components/seasons/SeasonPage):
 * clicking a season opens it, the pencil opens its Settings tab. Create = a short modal that then
 * opens the new season's page.
 */
function Seasons() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [seasons, setSeasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [alertModal, setAlertModal] = useState({ show: false, message: '', type: 'info' });
  const [confirmModal, setConfirmModal] = useState({ show: false, message: '', onConfirm: null });
  const [seasonTab, setSeasonTab] = useState('ongoing'); // 'ongoing' | 'finished'

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    setPage(1);
    if (searchQuery) {
      fetchData(true);
    } else {
      fetchData(false);
    }
  }, [searchQuery]);

  const fetchData = async (forceAll = false) => {
    setLoading(true);
    try {
      const limit = forceAll || searchQuery ? 10000 : 10000;
      const res = await axios.get(`/api/seasons?limit=${limit}`);
      setSeasons(res.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (id, name) => {
    setConfirmModal({
      show: true,
      message: t('createSeason.deleteConfirm', 'Delete the season "{{name}}"?', { name }),
      onConfirm: async () => {
        setConfirmModal({ show: false, message: '', onConfirm: null });
        try {
          await axios.delete(`/api/seasons/${id}`);
          setAlertModal({ show: true, message: 'Season deleted', type: 'success' });
          fetchData();
        } catch (err) {
          setAlertModal({ show: true, message: 'Failed to delete season', type: 'error' });
        }
      }
    });
  };

  const settingLabels = { admin_only_start: 'Admin Only Start', enforce_teams: 'Enforce Rosters', allow_stop: 'Allow !stop', skip_backup_load: 'Skip Backup Load', custom_avatars: 'Custom Avatars', steam_login_required: 'Steam Login for Veto' };

  // Helper: determine if a season is finished (end_date has passed)
  const isSeasonFinished = (s) => {
    if (!s.end_date) return false;
    const endDate = new Date(s.end_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    endDate.setHours(23, 59, 59, 999);
    return endDate < today;
  };

  const ongoingSeasons = seasons.filter(s => !isSeasonFinished(s));
  const finishedSeasons = seasons.filter(s => isSeasonFinished(s));
  const displayedSeasons = seasonTab === 'ongoing' ? ongoingSeasons : finishedSeasons;
  // buttons inside a card must not also open the season page
  const stop = (fn) => (e) => { e.stopPropagation(); fn(); };

  return (
    <div>
      <div style={{
        background: 'var(--bg-card)', border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-lg)', padding: '1.5rem 1.75rem',
      }}>
        {/* Header */}
        <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', paddingBottom: '1rem', borderBottom: '1px solid var(--border-subtle)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600, color: 'var(--text-primary)', fontFamily: 'var(--font-display)', letterSpacing: '-0.01em' }}>{t('nav.seasons', 'Seasons')}</h2>
            {/* Ongoing / Finished tabs */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', background: 'var(--bg-elevated)', borderRadius: 'var(--radius-md)', padding: '2px' }}>
              {[{ key: 'ongoing', label: t('seasons.ongoing', 'Ongoing'), count: ongoingSeasons.length, color: 'var(--accent-green)' }, { key: 'finished', label: t('matchStatus.finished', 'Finished'), count: finishedSeasons.length, color: 'var(--text-muted)' }].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => { setSeasonTab(tab.key); setPage(1); }}
                  style={{
                    padding: '4px 12px', borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontWeight: 600, fontSize: '0.72rem',
                    background: seasonTab === tab.key ? 'var(--bg-card)' : 'transparent',
                    border: seasonTab === tab.key ? '1px solid var(--border-subtle)' : '1px solid transparent',
                    color: seasonTab === tab.key ? tab.color : 'var(--text-muted)',
                    transition: 'all 0.15s',
                    display: 'flex', alignItems: 'center', gap: '0.3rem',
                    boxShadow: seasonTab === tab.key ? '0 1px 3px rgba(0,0,0,0.2)' : 'none',
                  }}
                >
                  {tab.label}
                  <span style={{
                    fontSize: '0.65rem', fontWeight: 700, minWidth: 16, textAlign: 'center',
                    padding: '0 4px', borderRadius: 100,
                    background: seasonTab === tab.key ? (tab.key === 'ongoing' ? 'rgba(34,197,94,0.1)' : 'rgba(255,255,255,0.04)') : 'rgba(255,255,255,0.04)',
                    color: seasonTab === tab.key ? tab.color : 'var(--text-muted)',
                  }}>{tab.count}</span>
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
              {[10, 50, 'all'].map(size => (
                <button
                  key={size}
                  onClick={() => { setPageSize(size); setPage(1); }}
                  style={{
                    padding: '3px 10px', borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontWeight: 500, fontSize: '0.7rem',
                    background: pageSize === size ? 'rgba(59,130,246,0.12)' : 'transparent',
                    border: pageSize === size ? '1px solid rgba(59,130,246,0.25)' : '1px solid var(--border-subtle)',
                    color: pageSize === size ? 'var(--accent-blue)' : 'var(--text-muted)',
                    transition: 'all 0.15s',
                    textTransform: 'uppercase',
                    letterSpacing: '0.03em',
                  }}
                >
                  {size === 'all' ? t('common.all', 'All') : size}
                </button>
              ))}
            </div>
            <input
              placeholder={t('seasons.searchPlaceholder', 'Search seasons...')}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              style={{
                padding: '0.35rem 0.75rem', fontSize: '0.78rem', borderRadius: 'var(--radius-sm)',
                background: 'var(--bg-input)', border: '1px solid var(--border-subtle)',
                color: 'var(--text-primary)', outline: 'none', minWidth: 160, maxWidth: 220, fontFamily: 'var(--font-sans)',
                transition: 'border-color 0.15s ease',
              }}
              onFocus={e => e.currentTarget.style.borderColor = 'var(--border-hover)'}
              onBlur={e => e.currentTarget.style.borderColor = 'var(--border-subtle)'}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
              {t('seasons.count', '{{count}} seasons', { count: displayedSeasons.length })}
            </span>
            <button onClick={() => setShowCreate(true)} style={{
              padding: '0.4rem 0.9rem', borderRadius: 'var(--radius-md)', cursor: 'pointer', fontWeight: 500, fontSize: '0.78rem',
              background: 'var(--accent-blue)', border: 'none', color: '#fff',
              display: 'flex', alignItems: 'center', gap: '0.35rem', transition: 'opacity 0.15s',
            }}
              onMouseOver={e => e.currentTarget.style.opacity = '0.85'}
              onMouseOut={e => e.currentTarget.style.opacity = '1'}
            >+ {t('seasons.createSeason', 'Create Season')}</button>
          </div>
        </div>

        {loading ? (
          <div style={{ color: 'var(--text-secondary)', padding: '2rem 0', textAlign: 'center', fontSize: '0.85rem' }}>{t('seasons.loading', 'Loading seasons...')}</div>
        ) : displayedSeasons.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3.5rem 1rem', color: 'var(--text-muted)' }}>
            <div style={{ marginBottom: '0.75rem' }}><IconClipboard size={32} color="var(--text-muted)" /></div>
            <div style={{ fontSize: '0.9rem', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '0.35rem' }}>{seasonTab === 'ongoing' ? t('seasons.noOngoing', 'No ongoing seasons') : t('seasons.noFinished', 'No finished seasons')}</div>
            <div style={{ fontSize: '0.75rem' }}>{seasonTab === 'ongoing' ? t('seasons.noOngoingHint', 'Create a season preset to speed up match creation') : t('seasons.noFinishedHint', 'Seasons appear here once their end date has passed')}</div>
          </div>
        ) : (
          <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
            {(pageSize === 'all' ? displayedSeasons : displayedSeasons.slice((page - 1) * pageSize, page * pageSize)).filter(s => {
              if (!searchQuery) return true;
              const q = searchQuery.toLowerCase();
              return (s.name || '').toLowerCase().includes(q);
            }).map(s => {
              const activeSettings = Object.entries(settingLabels).filter(([key]) => s[key]).map(([, label]) => label);
              const prefix = s.chat_prefix;
              const prefixColorOption = getPrefixColorOption(s.chat_prefix_color);

              return (
                <GlowCard key={s.id} accent="purple" className="season-card"
                  onClick={() => navigate(`/admin/seasons/${s.id}`)}
                  role="link" tabIndex={0} aria-label={t('seasonPage.openSeason', 'Open season {{name}}', { name: s.name })}
                  onKeyDown={e => { if (e.key === 'Enter' && e.target === e.currentTarget) navigate(`/admin/seasons/${s.id}`); }}
                  style={{
                  background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)',
                  padding: '0.75rem 1.25rem',
                  display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '1.25rem',
                  cursor: 'pointer', flexWrap: 'wrap',
                }}
                >
                  {/* Header: Name + Edit/Delete (row on mobile) */}
                  <div className="season-card-header" style={{ display: 'contents' }}>
                    <div style={{ flex: '1 1 0', minWidth: 160 }}>
                      <div style={{ fontSize: '0.88rem', fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.name}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.15rem', display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                        <span>Created {formatLocalDate(s.created_at)}</span>
                        <span onClick={e => e.stopPropagation()}><CreatedBy steamId={s.created_by_steam} name={s.created_by_name} /></span>
                        {s.start_date && s.end_date && (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', padding: '0.1rem 0.4rem', borderRadius: 'var(--radius-sm)', background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.12)', color: 'var(--accent-blue)', fontSize: '0.7rem', fontWeight: 500 }}>
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                            {formatLocalDate(s.start_date)} → {formatLocalDate(s.end_date)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="season-card-edit" style={{ display: 'flex', gap: '0.35rem', flexShrink: 0, order: 99 }}>
                      <button onClick={stop(() => navigate(`/admin/seasons/${s.id}/settings`))} title={t('seasonPage.editSettings', 'Edit settings')} aria-label={t('seasonPage.editSettings', 'Edit settings')} style={{
                        padding: '0.25rem 0.5rem', borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontSize: '0.7rem',
                        background: 'transparent', border: '1px solid var(--border-subtle)', color: 'var(--text-secondary)',
                        transition: 'all 0.15s',
                      }}
                        onMouseOver={e => { e.currentTarget.style.borderColor = 'var(--border-hover)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
                        onMouseOut={e => { e.currentTarget.style.borderColor = 'var(--border-subtle)'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
                      ><IconEdit size={12} /></button>
                      <button onClick={stop(() => handleDelete(s.id, s.name))} title={t('seasonPage.deleteSeason', 'Delete season')} aria-label={t('seasonPage.deleteSeason', 'Delete season')} style={{
                        padding: '0.25rem 0.5rem', borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontSize: '0.7rem',
                        background: 'transparent', border: '1px solid var(--border-subtle)', color: 'var(--text-muted)',
                        transition: 'all 0.15s',
                      }}
                        onMouseOver={e => { e.currentTarget.style.borderColor = 'rgba(239,68,68,0.25)'; e.currentTarget.style.color = 'var(--accent-red)'; }}
                        onMouseOut={e => { e.currentTarget.style.borderColor = 'var(--border-subtle)'; e.currentTarget.style.color = 'var(--text-muted)'; }}
                      ><IconTrash size={12} /></button>
                    </div>
                  </div>

                  {/* BO Badge */}
                  <span style={{
                    padding: '0.2rem 0.55rem', borderRadius: 'var(--radius-sm)', fontSize: '0.75rem', fontWeight: 600,
                    background: 'rgba(59,130,246,0.08)', color: 'var(--accent-blue)', border: '1px solid rgba(59,130,246,0.15)',
                    flexShrink: 0, fontFamily: 'var(--font-mono)',
                  }}>BO{s.best_of}</span>

                  {/* Mode Badge */}
                  <span style={{
                    padding: '0.2rem 0.55rem', borderRadius: 'var(--radius-sm)', fontSize: '0.7rem', fontWeight: 600,
                    background: s.mode === 'wingman' ? 'rgba(168,85,247,0.08)' : 'rgba(34,197,94,0.08)',
                    color: s.mode === 'wingman' ? '#a855f7' : 'var(--accent-green)',
                    border: s.mode === 'wingman' ? '1px solid rgba(168,85,247,0.15)' : '1px solid rgba(34,197,94,0.15)',
                    flexShrink: 0, textTransform: 'uppercase', letterSpacing: '0.04em',
                  }}>{s.mode === 'wingman' ? 'Wingman' : 'Active Duty'}</span>

                  {/* Veto Badge with hover tooltip */}
                  {s.veto_enabled ? (
                    <VetoBadge vetoStyle={s.veto_style} />
                  ) : null}

                  {/* Settings Tags */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', fontSize: '0.75rem', flex: '2 1 0', minWidth: 0 }}>
                    {activeSettings.map(label => (
                      <span key={label} style={{ padding: '0.15rem 0.4rem', borderRadius: 'var(--radius-sm)', background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.12)', color: 'var(--accent-amber)', whiteSpace: 'nowrap' }}>
                        {label}
                      </span>
                    ))}
                    {prefix && (
                      <span key="prefix" title={`Prefix color: ${prefixColorOption.name}`} style={{ padding: '0.15rem 0.4rem', borderRadius: 'var(--radius-sm)', background: `${prefixColorOption.hex}14`, border: `1px solid ${prefixColorOption.hex}33`, color: prefixColorOption.hex, fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
                        <span style={{ width: 7, height: 7, borderRadius: '50%', background: prefixColorOption.hex, display: 'inline-block' }} />
                        [{prefix}]
                      </span>
                    )}
                    {activeSettings.length === 0 && !prefix && (
                      <span style={{ padding: '0.15rem 0.4rem', borderRadius: 'var(--radius-sm)', background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border-subtle)', color: 'var(--text-muted)' }}>
                        Default settings
                      </span>
                    )}
                  </div>

                  {/* Actions - open the matching tab of the season page */}
                  <div className="season-card-actions" style={{ display: 'flex', gap: '0.35rem', flexShrink: 0 }}>
                    <button onClick={stop(() => navigate(`/admin/seasons/${s.id}/matches`))} title="View Matches" style={{
                      padding: '0.25rem 0.6rem', borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 500,
                      background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.12)', color: 'var(--accent-green)',
                      transition: 'all 0.15s', display: 'inline-flex', alignItems: 'center', gap: 4,
                    }}
                      onMouseOver={e => e.currentTarget.style.background = 'rgba(34,197,94,0.12)'}
                      onMouseOut={e => e.currentTarget.style.background = 'rgba(34,197,94,0.06)'}
                    ><IconSwords size={11} /> Matches</button>
                    <button onClick={stop(() => navigate(`/admin/seasons/${s.id}/leaderboard`))} title={t('leaderboard.title', 'Leaderboard')} style={{
                      padding: '0.25rem 0.6rem', borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 500,
                      background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.12)', color: 'var(--accent-blue)',
                      transition: 'all 0.15s', display: 'inline-flex', alignItems: 'center', gap: 4,
                    }}
                      onMouseOver={e => e.currentTarget.style.background = 'rgba(59,130,246,0.12)'}
                      onMouseOut={e => e.currentTarget.style.background = 'rgba(59,130,246,0.06)'}
                    ><IconBarChart size={11} /> {t('leaderboard.title', 'Leaderboard')}</button>
                    <button onClick={stop(() => navigate(`/admin/seasons/${s.id}/brackets`))} title="Brackets" style={{
                      padding: '0.25rem 0.6rem', borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 500,
                      background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.12)', color: 'var(--accent-amber)',
                      transition: 'all 0.15s', display: 'inline-flex', alignItems: 'center', gap: 4,
                    }}
                      onMouseOver={e => e.currentTarget.style.background = 'rgba(245,158,11,0.12)'}
                      onMouseOut={e => e.currentTarget.style.background = 'rgba(245,158,11,0.06)'}
                    >
                      Brackets
                    </button>
                  </div>
                </GlowCard>
              );
            })}
          </div>
          {pageSize !== 'all' && displayedSeasons.length > pageSize && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', marginTop: '1.25rem' }}>
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1}
                style={{
                  padding: '4px 12px', borderRadius: 'var(--radius-sm)', cursor: page <= 1 ? 'not-allowed' : 'pointer', fontWeight: 500, fontSize: '0.75rem',
                  background: 'transparent', border: '1px solid var(--border-subtle)', color: page <= 1 ? 'var(--text-muted)' : 'var(--text-secondary)',
                  transition: 'all 0.15s',
                }}
              >Prev</button>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                Page {page} of {Math.ceil(displayedSeasons.length / pageSize)}
              </span>
              <button
                onClick={() => setPage(p => Math.min(Math.ceil(displayedSeasons.length / pageSize), p + 1))}
                disabled={page >= Math.ceil(displayedSeasons.length / pageSize)}
                style={{
                  padding: '4px 12px', borderRadius: 'var(--radius-sm)', cursor: page >= Math.ceil(displayedSeasons.length / pageSize) ? 'not-allowed' : 'pointer', fontWeight: 500, fontSize: '0.75rem',
                  background: 'transparent', border: '1px solid var(--border-subtle)', color: page >= Math.ceil(displayedSeasons.length / pageSize) ? 'var(--text-muted)' : 'var(--text-secondary)',
                  transition: 'all 0.15s',
                }}
              >Next</button>
            </div>
          )}
          </>
        )}
      </div>

      <CreateSeasonModal isOpen={showCreate} onClose={() => setShowCreate(false)}
        onCreated={(s) => { setShowCreate(false); navigate(`/admin/seasons/${s.id}/settings`); }} />
      <AlertModal show={alertModal.show} message={alertModal.message} type={alertModal.type} onClose={() => setAlertModal({ ...alertModal, show: false })} />
      <ConfirmModal show={confirmModal.show} message={confirmModal.message} onConfirm={confirmModal.onConfirm} onClose={() => setConfirmModal({ ...confirmModal, show: false })} />
    </div>
  );
}

export default Seasons;
